import {
  UseInfiniteQueryOptions,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import clientApi from '~/clientApi';
import { AGENCY_SAVED_LISTS, SAVED_LISTS } from '~/queries/keys';
import { selectTalentManager } from '~/selectors/talent-manager';
import { ManagerType } from '~/types';

export interface IUseGetSavedListsOptions {
  creatorIds?: string[];
  enableAgencyLists?: boolean;
  name?: string[];
  page?: number;
  pageSize?: number;
  properties?: string[];
  saved?: boolean;
}

export interface IGetSavedLists
  extends Omit<IUseGetSavedListsOptions, 'enableAgencyLists'> {
  agencyId: string;
  isAgencyEnabled: boolean;
  name?: string[];
  properties?: string[];
}

const getSavedLists = ({
  agencyId,
  creatorIds,
  isAgencyEnabled,
  name,
  page,
  pageSize,
  properties,
  saved,
}: IGetSavedLists) => {
  if (agencyId && isAgencyEnabled) {
    return clientApi.agencies.getLists({
      filter: [
        `saved:${saved}`,
        ...(!isEmpty(name) ? [`name:${name}`] : []),
        ...(creatorIds?.length ? [`creators:${creatorIds.join(',')}`] : []),
      ],
      id: agencyId,
      page,
      pageSize,
      properties,
      _isImpersonatingDisabled: true,
    });
  }

  return clientApi.savedLists.index({
    _isImpersonatingDisabled: true,
    creators: creatorIds,
    name,
    page,
    pageSize,
    properties,
    saved,
  });
};

export const useGetSavedLists = (
  {
    creatorIds,
    enableAgencyLists = false,
    name = [],
    page = 1,
    pageSize = 10,
    properties,
    saved = false,
  }: IUseGetSavedListsOptions,
  { enabled }: Pick<UseInfiniteQueryOptions, 'enabled'> = {},
) => {
  const manager: ManagerType = useSelector(selectTalentManager);
  const agencyId = manager?.agency?.id;
  const isAgencyEnabled = enableAgencyLists && !!manager?.agency?.enabled;

  const queryKey = isAgencyEnabled
    ? [AGENCY_SAVED_LISTS, { creatorIds, pageSize, saved, name, properties }]
    : [SAVED_LISTS, { creatorIds, pageSize, saved, name, properties }];

  const { data: raw, ...query } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = page }) =>
      getSavedLists({
        agencyId,
        creatorIds,
        isAgencyEnabled,
        name,
        page: pageParam,
        pageSize,
        properties,
        saved,
      }),
    getNextPageParam: (page, pages) =>
      Math.floor(pages.length * pageSize) <= (page?.totalItems ?? 0)
        ? pages.length + 1
        : undefined,
    staleTime: 60 * 1000 * 2, // 2 minutes,
    enabled,
  });

  const data = useMemo(
    () =>
      raw?.pages.reduce(
        (acc, page) => {
          acc.results = [...acc.results, ...page.results];
          acc.totalItems = page.totalItems;
          return acc;
        },
        {
          results: [],
          totalItems: 0,
        },
      ),
    [raw?.pages],
  );

  return { data, queryKey, ...query };
};
